<!-- Layer visibility and entity/application focus controls for the business view toolbar. -->
<!-- Available throughout the stekz hierarchy (domain, product, process pages). -->
<template>
    <ButtonGroup v-if="stek">
        <FocusPanel
            :focusable-entities="focusableEntities"
            :focused-entity-id="focusedEntityId"
            :focusable-applications="
                devModeEnabled ? focusableApplications : []
            "
            :focused-application-id="focusedApplicationId"
            @update:focused-entity-id="handleEntityFocusChange"
            @update:focused-application-id="handleApplicationFocusChange"
        />
        <LayerTogglePanel
            :layers="visibleLayers"
            @toggle="setLayerVisibility"
        />
    </ButtonGroup>
</template>

<script lang="ts" setup>
import ButtonGroup from "primevue/buttongroup";
import { computed } from "vue";

import FocusPanel from "@/components/BPMN/FocusPanel.vue";
import LayerTogglePanel from "@/components/BPMN/LayerTogglePanel.vue";
import {
    clearApplicationFocus,
    focusedApplicationId,
    setApplicationFocus,
} from "@/composables/bpmn-manager/application-focus";
import {
    clearFocus,
    focusedEntityId,
    setFocus,
} from "@/composables/bpmn-manager/entity-focus";
import {
    setLayerVisibility,
    useLayerControls,
} from "@/composables/bpmn-manager/layer";
import { setPathTraceActive } from "@/composables/bpmn-manager/path-trace";
import { useStekManager } from "@/composables/stek-manager";
import { useApplicationsManager } from "@/stores/applications-store";
import { useBusinessEntitiesManager } from "@/stores/business-entities-store";

const { stek } = useStekManager();
const devModeEnabled = computed(() => stek.value?.dev_mode_enabled ?? false);
const complianceEnabled = computed(
    () => stek.value?.compliance_enabled ?? false
);

const { layers } = useLayerControls();
const visibleLayers = computed(() =>
    layers.value.filter((l) => {
        if (l.type === "applications") return devModeEnabled.value;
        if (l.type === "compliancy") return complianceEnabled.value;
        return true;
    })
);

const { entities } = useBusinessEntitiesManager();
const { applications } = useApplicationsManager();

const focusableEntities = computed(() =>
    (entities.value ?? []).map((e) => ({ entity_id: e.identity, name: e.name }))
);

const focusableApplications = computed(() =>
    applications.value.map((a) => ({ identity: a.identity, name: a.name }))
);

const handleEntityFocusChange = (entityId: string | null) => {
    if (entityId) {
        clearApplicationFocus();
        setPathTraceActive(false);
        setFocus(entityId);
    } else {
        clearFocus();
    }
};

const handleApplicationFocusChange = (applicationId: string | null) => {
    if (applicationId) {
        clearFocus();
        setPathTraceActive(false);
        setApplicationFocus(applicationId);
    } else {
        clearApplicationFocus();
    }
};
</script>
